/**
 * Die Rechnungen des **Texts** (`kind: 'text'`, CONTEXT.md „Text“) — Größe,
 * Lage, Farbe. Ohne React-Bezug wie die übrigen `utils/`.
 *
 * Seine Maße folgen immer dem Inhalt; was hier steht, entscheidet nur, wie
 * groß die Schrift ist und wo er beginnt.
 */

import { snapToGrid } from './canvasAlignment'
import { CANVAS_NOTE_COLORS, type CanvasNoteColorKey } from './canvasNoteStyle'

/** Die Stufen des Größen-Menüs (FigJam-Muster) — Schlüssel für die Beschriftung, Größe in px. */
export const CANVAS_TEXT_SIZES = [
  { key: 'small', size: 16 },
  { key: 'medium', size: 24 },
  { key: 'large', size: 40 },
  { key: 'extraLarge', size: 64 },
  { key: 'huge', size: 96 },
] as const

export type CanvasTextSizeKey = (typeof CANVAS_TEXT_SIZES)[number]['key']

/** Die Größe eines Texts, an dem noch nichts eingestellt wurde. */
export const DEFAULT_CANVAS_TEXT_SIZE = 24

/**
 * Die Grenzen jeder Schriftgröße — für das Zahlenfeld, das Aufziehen und die
 * Ecken. Unter 8 ist nichts mehr zu lesen; über 400 füllt ein einzelnes Wort
 * den Schirm.
 */
export const MIN_CANVAS_TEXT_SIZE = 8
export const MAX_CANVAS_TEXT_SIZE = 400

/**
 * Die Zeilenhöhe als Vielfaches der Schriftgröße. Eine Zahl für Darstellung
 * UND Rechnung: Das aufgezogene Quadrat wird über sie zur Schriftgröße, und
 * der Text soll danach genau so hoch sein wie das Quadrat.
 */
export const CANVAS_TEXT_LINE_HEIGHT = 1.2

/**
 * Unter dieser Höhe (in Canvas-Pixeln) trägt ein Text keinen **Tauschpunkt** —
 * der Ring verdeckte das Wort, das er tauschen soll (CONTEXT.md, Text).
 */
export const MIN_CANVAS_TEXT_SWAP_HEIGHT = 32

/** Rundet und begrenzt eine Schriftgröße. */
export function clampCanvasTextSize(size: number): number {
  if (!Number.isFinite(size)) return DEFAULT_CANVAS_TEXT_SIZE
  return Math.min(MAX_CANVAS_TEXT_SIZE, Math.max(MIN_CANVAS_TEXT_SIZE, Math.round(size)))
}

/**
 * Die Schriftgröße, die ein **aufgezogenes Quadrat** festlegt: Seine Seite ist
 * die Höhe einer Zeile.
 *
 * @example
 * canvasTextSizeFromSide(48) // 40
 */
export function canvasTextSizeFromSide(side: number): number {
  return clampCanvasTextSize(side / CANVAS_TEXT_LINE_HEIGHT)
}

/**
 * Die Schriftgröße nach einem Zug an einer Ecke: im selben Verhältnis wie die
 * Höhe. Ohne brauchbare Ausgangshöhe bleibt sie, wie sie war.
 */
export function scaleCanvasTextSize(size: number, fromHeight: number, toHeight: number): number {
  if (!(fromHeight > 0) || !(toHeight > 0)) return size
  return clampCanvasTextSize(size * (toHeight / fromHeight))
}

/** Die Stufe, deren Größe genau diese ist — sonst `null` (dann zeigt das Feld die Zahl). */
export function canvasTextSizePreset(size: number | null | undefined): CanvasTextSizeKey | null {
  return CANVAS_TEXT_SIZES.find(preset => preset.size === size)?.key ?? null
}

/**
 * Die Schriftgröße eines Texts: sein Feld, sonst die Vorgabe. Eine Zeile ohne
 * Wert (vor der Migration, oder aus einer älteren App-Fassung) bekommt so
 * trotzdem eine lesbare Größe.
 */
export function canvasTextSizeOf(note: { fontSize?: number | null }): number {
  return note.fontSize ?? DEFAULT_CANVAS_TEXT_SIZE
}

/**
 * Wo ein Text beginnt, wenn der Zeiger HIER klickt: seine linke obere Ecke am
 * Fadenkreuz, aufs Punktraster — anders als bei den Zetteln, die mittig unter
 * dem Zeiger landen. Man zeigt auf die Stelle, an der man zu schreiben beginnt.
 */
export function getCanvasTextPosition(pointer: { x: number; y: number }, grid: number): { x: number; y: number } {
  return { x: snapToGrid(pointer.x, grid), y: snapToGrid(pointer.y, grid) }
}

/**
 * Das **aufgezogene Quadrat** zwischen Anfang und Zeiger: Die Seite ist die
 * längere der beiden Strecken, die Ecke am Anfang bleibt stehen — gezogen
 * wird in jede Richtung.
 */
export function canvasTextSquare(
  origin: { x: number; y: number },
  pointer: { x: number; y: number },
): { x: number; y: number; side: number } {
  const dx = pointer.x - origin.x
  const dy = pointer.y - origin.y
  const side = Math.max(Math.abs(dx), Math.abs(dy))
  return {
    x: dx < 0 ? origin.x - side : origin.x,
    y: dy < 0 ? origin.y - side : origin.y,
    side,
  }
}

/**
 * Die Zeichen je Schriftgröße, mit denen ein noch ungemessener Text geschätzt
 * wird. Er braucht beim Anlegen irgendeine Breite (React Flow versteckt einen
 * Node ohne Maße); die Messung ersetzt sie im ersten Frame.
 */
const ESTIMATED_CHAR_WIDTH = 0.55

/**
 * Wofür beim Schätzen eines LEEREN Texts gerechnet wird: so viele Zeichen wie
 * der Platzhalter („Text hinzufügen“ / „Add text“ liegen dazwischen). Die
 * Übersetzung selbst gehört nicht hierher.
 */
export const CANVAS_TEXT_PLACEHOLDER_ESTIMATE = 'x'.repeat(12)

/** Die geschätzten Maße eines Texts mit diesem Inhalt, bevor er gemessen ist. */
export function estimateCanvasTextSize(text: string, fontSize: number): { width: number; height: number } {
  const lines = text.split('\n')
  const longest = Math.max(1, ...lines.map(line => line.length))
  return {
    width: Math.ceil(longest * fontSize * ESTIMATED_CHAR_WIDTH),
    height: Math.ceil(lines.length * fontSize * CANVAS_TEXT_LINE_HEIGHT),
  }
}

/**
 * Die **Schriftfarbe** eines Texts.
 *
 * `null` heißt **Standard**: die Tinte des Themes (`--foreground`), dunkel auf
 * hellem, hell auf dunklem Grund. Eine Palettenfarbe ist der Ton, den der
 * **Farbwähler** zeigt (`swatch`) — der Wähler ist die Vorschau, was man dort
 * anklickt, steht danach auf der Fläche.
 */
export function getCanvasTextInk(key: string | null | undefined): string {
  if (!key) return 'var(--foreground)'
  return CANVAS_NOTE_COLORS.find(color => color.key === key)?.swatch ?? 'var(--foreground)'
}

/**
 * Die Farben des **Hervorhebens** (Textmarker, ADR 0029) — dieselbe Palette
 * und derselbe Wähler wie die Schriftfarbe, beide Stufen (gewünscht
 * 2026-09-23: mehr Auswahl, und den Wähler kennt man schon).
 */
export type CanvasTextHighlightKey = CanvasNoteColorKey

/** Ein Marker ohne bekannte Farbe — gelb, die Farbe eines Textmarkers schlechthin. */
export const DEFAULT_CANVAS_TEXT_HIGHLIGHT: CanvasTextHighlightKey = 'yellow'

/**
 * Der Ton eines Textmarkers — der Tropfen aus dem Wähler (`swatch`), wie bei
 * der Schriftfarbe: Was man dort anklickt, steht danach hinter dem Text.
 */
export function getCanvasTextHighlight(key: string): string {
  return (CANVAS_NOTE_COLORS.find(color => color.key === key)
    ?? CANVAS_NOTE_COLORS.find(color => color.key === DEFAULT_CANVAS_TEXT_HIGHLIGHT)!).swatch
}

/**
 * Wie weit ein Text beim Wachsen nach links rückt, damit seine Ausrichtung
 * die Kante hält (gemeldet 2026-09-23): linksbündig bleibt die linke Kante
 * stehen, zentriert die Mitte, rechtsbündig die rechte. `grow` ist die neue
 * Breite minus die alte; beim Schrumpfen ist es negativ, der Text rückt dann
 * nach rechts.
 *
 * @example
 * canvasTextAnchorShift('center', 40) // -20
 */
export function canvasTextAnchorShift(align: 'left' | 'center' | 'right' | null | undefined, grow: number): number {
  if (align === 'center') return -grow / 2
  if (align === 'right') return -grow
  return 0
}
